import { useEffect } from 'react'

/**
 * El teclado FÍSICO cargando los dígitos de un diálogo del POS.
 *
 * Los dos diálogos que piden un número —la cantidad de un producto y el monto
 * del cobro sin detalle— tienen su teclado en pantalla y el input en
 * `readOnly`, y eso es deliberado: en la tablet del mostrador un input
 * editable hace saltar el teclado del sistema, que tapa media pantalla justo
 * cuando hay que ver el importe. Lo que faltaba es la otra mitad: en una caja
 * con TECLADO —la PC del mostrador, o el navegador del escritorio— tipear no
 * hacía nada, así que había que cargar un peso de cuatro dígitos con el mouse.
 * Es el mismo error que la tecla rápida del medio de pago: la pantalla ofrece
 * una forma de cargar y la que la persona tiene en la mano no está enganchada.
 *
 * Se escucha en `window` y no en el input porque el foco se va al botón del
 * teclado en pantalla en cuanto alguien toca uno: con el handler en el input,
 * tocar una tecla con el mouse dejaba de nuevo sin funcionar el teclado.
 *
 * Y se deja pasar lo que se está TIPEANDO en un campo de verdad (el
 * comprobante, la referencia, el entregado), con la misma regla que
 * `useTeclaDelMedio`: un input `readOnly` no cuenta como campo de texto
 * —justamente es el que muestra estos dígitos—, uno editable sí.
 */
export function useTecladoNumerico(
  setDigitos: (f: (v: string) => string) => void,
  {
    max = 9,
    activo = true,
    agregar,
  }: {
    max?: number
    activo?: boolean

    /**
     * Cómo se agrega una tecla a lo tipeado. Se inyecta porque los dos
     * diálogos tienen modelos DISTINTOS y no se pueden unificar: el de
     * cantidad interpreta los dígitos en la escala de la unidad («1250» son
     * 1,250 kg) y el del importe acepta una coma con dos decimales. Con la
     * concatenación pelada acá adentro, la coma del cobro no entraba —era la
     * tecla que no hacía nada— y agregarla a secas rompería la cantidad.
     */
    agregar?: (actual: string, tecla: string) => string
  } = {},
) {
  useEffect(() => {
    if (!activo) return

    function alApretar(e: KeyboardEvent) {
      // Con un modificador es un atajo del navegador o del sistema.
      if (e.ctrlKey || e.altKey || e.metaKey) return

      const activo = document.activeElement
      const editable =
        (activo instanceof HTMLInputElement && !activo.readOnly && !activo.disabled) ||
        activo instanceof HTMLTextAreaElement ||
        (activo instanceof HTMLElement && activo.isContentEditable)

      if (editable) return

      // Con un `agregar` propio, la coma y el punto del numpad también son
      // teclas de este campo: el que decide si entran es el modelo.
      const suya = agregar ? /^[0-9,.]$/.test(e.key) : /^[0-9]$/.test(e.key)

      if (suya) {
        e.preventDefault()
        setDigitos((v) => (agregar ? agregar(v, e.key) : (v + e.key).slice(0, max)))
        return
      }

      if (e.key === 'Backspace') {
        e.preventDefault()
        setDigitos((v) => v.slice(0, -1))
        return
      }

      // Delete borra todo, como la «C» del teclado en pantalla. No se usa
      // Escape: en un `<dialog>` esa tecla CIERRA, y es lo que corresponde.
      if (e.key === 'Delete') {
        e.preventDefault()
        setDigitos(() => '')
      }
    }

    window.addEventListener('keydown', alApretar)
    return () => window.removeEventListener('keydown', alApretar)
  }, [setDigitos, max, activo, agregar])
}
